// src/App.tsx
import "../Report.css";

import EmissionsBreakdownChart from "@/pages/communication/common/EmissionsBreakdownChart";

import ReportHeader from "@/components/ReportHeader";
import ReportFooter from "@/components/ReportFooter";
import { Car } from "lucide-react";
import { DataPill } from "@/pages/communication/common/DataPill";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { CompanySummary, VehicleEmission } from "@/types";
import { getApiUrl } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { categoriesAtom } from "@/store/categories";
import { useTranslation } from "react-i18next";

interface IntroPageProps {
  title: string;
  page?: number;
}

export const VehiculosPropiosPage = ({ title, page }: IntroPageProps) => {
  const { token } = useAuth();
  const { t } = useTranslation();

  const [company] = useAtom(companyAtom);
  const [dates] = useAtom(datesAtom);
  const [categories] = useAtom(categoriesAtom);

  const { data } = useQuery<VehicleEmission[]>({
    queryKey: [
      "vehicles-emissions",
      company?.id,
      dates?.fromDate,
      dates?.toDate,
    ],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `companies/${company?.id}/vehicles-emissions/?start_date=${dates?.fromDate}&end_date=${dates?.toDate}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!company && !!dates,
  });

  const { data: emissionsSummary } = useQuery<CompanySummary>({
    queryKey: [
      "emissions-summary",
      company?.id,
      dates?.fromDate,
      dates?.toDate,
    ],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `companies/${company?.id}/emissions-summary/?start_date=${dates?.fromDate}&end_date=${dates?.toDate}&categories=${categories?.join(",")}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!company && !!dates,
  });

  const renderData = () => {
    return data?.map((vehicle: VehicleEmission) => {
      const item = {
        icon: <Car className="h-6 w-6" />,
        label: String(vehicle?.license_plate),
        value: Number(vehicle?.emissions),
      };
      return item;
    });
  };

  return (
    <section className="pdf-page">
      <ReportHeader title={title} />
      <div className="pdf-content text-xl">
        <div className="px-2 grid grid-cols-1 gap-4 w-[600px]">
          <DataPill
            title={t(
              "Emisiones totales procedentes de vehículos de la empresa",
            )}
            value={String(emissionsSummary?.categories?.consumption.toFixed(2))}
            unit="ton CO2eq"
            icon={<Car className="h-8 w-8 text-primary" />}
          />
        </div>
        <div className="mt-4 w-[800px]">
          <EmissionsBreakdownChart data={renderData() || []} />
        </div>
      </div>
      <ReportFooter page={page} />
    </section>
  );
};
